/**
 * components/locks/MeasureConnectorFlushThickness.tsx — Spessore REALE del
 * connettore, misurato sulla mesh GLB (29-09-2026, richiesta utente: "ok ora
 * troviamo quello spessore [misurato sulla mesh] per posizionare il
 * lockstop").
 *
 * PERCHE': ne' `hole.position.z` (quota JSON) ne' `screw_edge_thickness` (DB,
 * usato per i calcoli di resistenza) coincidono con la vera geometria della
 * mesh — verificato su 2 asset (LOCKT3580: mesh 5.00mm contro 7.25/7.0mm
 * dichiarati; LOCKT100265: mesh 6.00mm contro 7.75/8.0mm), stesso scarto
 * sistematico su entrambi. Per il filo (asse Z) serve quindi la mesh vera,
 * non una quota dichiarata.
 *
 * FRAME (corretto 29-09-2026 — la prima versione cercava sull'asse sbagliato):
 * dopo `Object3D.localToWorld` (nodo GLTF gia' caricato da drei/three, NON i
 * byte grezzi del file — quelli sono in un frame diverso, vedi sotto) il
 * connettore e' GIA' nello stesso frame del JSON, SENZA permutazione: bbox
 * verificato su LOCKT100265, world.x ∈ [-50,50] (= assembly.width/2),
 * world.y ∈ [-126,126] (= module.height/2), **world.z ∈ [0, 21.5]** — 21.5
 * e' esattamente `connector_module.thickness` di quell'asset. Quindi
 * world.x=json.x, world.y=json.y, world.z=json.z: la sonda usa (x,y) del
 * foro e lo spessore si misura sull'estensione di world.z, non world.y (bug
 * della prima versione, trovato dal log "nessun vertice trovato" — la
 * ricerca cercava vicino a world.y=json.y ma poi guardava l'estensione di
 * world.y stesso, un frame diverso da quello confermato coi byte grezzi del
 * file usato nell'indagine iniziale via script Node — QUELLA conversione
 * (native = (x,-z,y)) vale SOLO leggendo il buffer POSITION senza passare da
 * three.js/GLTFLoader, non dopo `localToWorld`).
 *
 * COME: vicino a un punto di sonda (un foro standard qualunque del
 * connettore, la Z e' la stessa per tutti — verificato su ogni asset visto
 * finora), si cercano i vertici REALI della mesh in un raggio fisso (nel
 * piano XY) e si misura la loro estensione lungo Z. Il raggio (6mm) e' stato
 * scelto perche' su LOCKT3580/LOCKT100265 dava una lettura STABILE nel primo
 * (sbagliato) tentativo sull'asse Y — da ricalibrare ora che si guarda Z, ma
 * tenuto come punto di partenza ragionevole (ordine di grandezza di un foro).
 *
 * Il risultato e' gia' lo spessore da usare DIRETTAMENTE come Z locale del
 * lockstop (Z=0 = faccia a contatto con la trave, confermato dall'utente):
 * lo span misurato va da 0 (quella faccia) all'altra (la faccia esterna),
 * quindi span = distanza fra le due facce = esattamente l'anchorZ cercato.
 *
 * PERCHE' QUI (render layer) E NON in `model/`: stesso motivo di
 * `ConnectorMeshOffset` — solo qui si puo' caricare/leggere il GLB (SPEC §6
 * vieta a `model/`/`transforms/` di toccare three.js/asset). Component
 * pattern: render-prop, stesso schema Suspense-safe di `GlbView`/
 * `ConnectorMeshOffset` (guard su url/probe null PRIMA di un componente
 * interno che chiama `useGLTF` incondizionatamente).*/

import { useGLTF } from "@react-three/drei";
import { useMemo } from "react";
import * as THREE from "three";

/** Raggio di ricerca (mm) — vedi doc-comment in cima per la calibrazione. */
const SEARCH_RADIUS_MM = 6;

export interface MeasureConnectorFlushThicknessProps {
  /** GLB URL blob; null → nessuna misura (children riceve null, il chiamante ricade sul fallback). */
  url: string | null;
  /**
   * (x,y) di un foro standard qualunque del connettore, STESSO frame di
   * `hole.position` (dopo `localToWorld` non serve nessuna conversione —
   * vedi doc-comment in cima). null → nessuna misura.*/
  probeXY: readonly [number, number] | null;
  /** Render-prop: riceve lo spessore misurato (mm) o null se non calcolabile. */
  children: (flushThicknessMm: number | null) => React.ReactNode;
}

export function MeasureConnectorFlushThickness({ url, probeXY, children }: MeasureConnectorFlushThicknessProps) {
  if (!url || !probeXY) return <>{children(null)}</>;
  return (
    <MeasureConnectorFlushThicknessInner url={url} probeXY={probeXY}>
      {children}
    </MeasureConnectorFlushThicknessInner>
  );
}

function MeasureConnectorFlushThicknessInner({
  url,
  probeXY,
  children,
}: {
  url: string;
  probeXY: readonly [number, number];
  children: (flushThicknessMm: number | null) => React.ReactNode;
}) {
  const { scene } = useGLTF(url);
  // `probeXY` arriva come array letterale ricreato a ogni render del chiamante:
  // la memo deve dipendere dai due numeri, non dall'array, altrimenti il loop
  // su tutti i vertici (e i console.log) rigira a ogni render.
  const [px, py] = probeXY;
  const flushThicknessMm = useMemo(() => {
    //Sonda scollegata dalla scena (nessun genitore), stesso schema di
    //`ConnectorMeshOffset`: il frame nativo riflette solo la geometria, senza
    //position/quaternion dell'istanza applicati altrove nel render tree.
    const probe = scene.clone(true);
    probe.updateMatrixWorld(true);
    let minZ = Infinity;
    let maxZ = -Infinity;
    let meshCount = 0;
    let vertexCount = 0;
    let nearestDist = Infinity;
    let nearestVertex: [number, number, number] | null = null;
    const bboxMin = new THREE.Vector3(Infinity, Infinity, Infinity);
    const bboxMax = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
    const v = new THREE.Vector3();
    probe.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      meshCount += 1;
      const posAttr = obj.geometry.attributes.position;
      if (!posAttr) return;
      for (let i = 0; i < posAttr.count; i++) {
        v.fromBufferAttribute(posAttr, i);
        obj.localToWorld(v);
        vertexCount += 1;
        bboxMin.min(v);
        bboxMax.max(v);
        const d = Math.hypot(v.x - px, v.y - py);
        if (d < nearestDist) {
          nearestDist = d;
          nearestVertex = [v.x, v.y, v.z];
        }
        if (d < SEARCH_RADIUS_MM) {
          minZ = Math.min(minZ, v.z);
          maxZ = Math.max(maxZ, v.z);
        }
      }
    });
    if (!Number.isFinite(minZ) || !Number.isFinite(maxZ)) {
      // Righe separate con soli valori primitivi (29-09-2026): l'anteprima
      // console di un oggetto annidato si tronca nel copia-incolla, questi
      // numeri sono quelli che servono per capire se e' un problema di
      // scala/frame (sonda "vicina ma fuori raggio") o di lettura mesh
      // (nessun vertice trovato proprio).
      console.warn("[MeasureConnectorFlushThickness] nessun vertice trovato vicino alla sonda");
      console.warn("  url =", url);
      console.warn("  probeXY (x,y) =", px, py);
      console.warn("  searchRadiusMm =", SEARCH_RADIUS_MM);
      console.warn("  meshCount =", meshCount, " vertexCount =", vertexCount);
      console.warn("  nearestDist (mm, XY dalla sonda) =", nearestDist);
      console.warn("  nearestVertex (x,y,z) =", nearestVertex?.[0], nearestVertex?.[1], nearestVertex?.[2]);
      console.warn("  bboxMin (x,y,z) =", bboxMin.x, bboxMin.y, bboxMin.z);
      console.warn("  bboxMax (x,y,z) =", bboxMax.x, bboxMax.y, bboxMax.z);
      return null;
    }
    const measured = maxZ - minZ;
    console.log("[MeasureConnectorFlushThickness] spessore misurato");
    console.log("  url =", url);
    console.log("  probeXY (x,y) =", px, py);
    console.log("  zRange (min,max) =", minZ, maxZ);
    console.log("  measuredThicknessMm =", measured);
    return measured;
  }, [scene, px, py, url]);
  return <>{children(flushThicknessMm)}</>;
}
